import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ApiError } from '../api/client'
import { jobsApi } from '../api/jobs'
import type { JobLine, JobLineStatus } from '../api/types'
import { useAuth } from '../auth/useAuth'
import { Alert } from '../components/Alert'
import { Modal } from '../components/Modal'
import { JobStatusPill, LineStatusPill, StatusPill } from '../components/StatusPill'
import { EmptyState, ErrorState, LoadingState } from '../components/States'
import { isJobLive, useJobPolling } from '../hooks/useJobPolling'
import { formatCredits, formatDateTime, formatInt, formatTime } from '../lib/format'

type LineFilter = JobLineStatus | 'All'

const FILTERS: LineFilter[] = ['All', 'Error', 'Skipped', 'Success']

const LOG_TONE = { Info: 'idle', Warning: 'warn', Error: 'err' } as const

function headline(type: string, status: string): string {
  // The page is shared by Import and Export jobs; the word should match the job
  // being shown, not always say "Import".
  const word = type === 'Export' ? 'Export' : type === 'Delete' ? 'Delete' : 'Import'
  switch (status) {
    case 'Completed':
      return `${word} finished`
    case 'PartialSuccess':
      return `${word} finished with errors`
    case 'Failed':
      return `${word} failed`
    case 'Cancelled':
      return `${word} cancelled`
    default:
      return `${word} in progress`
  }
}

export function JobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>()
  const { refreshUser } = useAuth()
  const { job, error, loading } = useJobPolling(jobId)

  const [filter, setFilter] = useState<LineFilter>('Error')
  const [lines, setLines] = useState<JobLine[]>([])
  const [linesLoading, setLinesLoading] = useState(true)
  const [linesError, setLinesError] = useState<ApiError | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [drafts, setDrafts] = useState<Record<string, Record<string, string | null>>>({})
  const [saving, setSaving] = useState<string | null>(null)
  const [retrying, setRetrying] = useState(false)
  const [banner, setBanner] = useState<string | null>(null)
  const [undoOpen, setUndoOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const loadLines = useCallback(async () => {
    if (!jobId) return
    setLinesLoading(true)
    setLinesError(null)
    try {
      const page = await jobsApi.lines(jobId, filter === 'All' ? undefined : filter, 1, 200)
      setLines(page.items)
      // Failed rows are pre-selected so "Retry failed" is one click, per the design.
      setSelected(new Set(page.items.filter((line) => line.status === 'Error').map((line) => line.id)))
      setDrafts({})
    } catch (cause: unknown) {
      setLinesError(cause instanceof ApiError ? cause : new ApiError(0, 'NETWORK', String(cause)))
    } finally {
      setLinesLoading(false)
    }
  }, [jobId, filter])

  useEffect(() => {
    // The compiler lint dislikes the loading flag being raised synchronously,
    // but this is the same fetch-with-spinner pattern `useAsync` encapsulates;
    // the lines list needs local mutation after an inline fix, so it stays here.
    // oxlint-disable-next-line react/set-state-in-effect
    void loadLines()
  }, [loadLines])

  /** Columns shown in the fix grid, derived from whatever the lines carry. */
  const fields = useMemo(() => {
    const seen: string[] = []
    for (const line of lines) {
      for (const key of Object.keys(line.values)) if (!seen.includes(key)) seen.push(key)
    }
    return seen.slice(0, 6)
  }, [lines])

  const applyEdit = async (line: JobLine) => {
    const draft = drafts[line.id]
    if (!draft || !jobId) return
    setSaving(line.id)
    setBanner(null)
    try {
      const updated = await jobsApi.updateLine(jobId, line.id, { ...line.values, ...draft })
      setLines((current) => current.map((item) => (item.id === line.id ? updated : item)))
      setDrafts((current) => {
        const next = { ...current }
        delete next[line.id]
        return next
      })
    } catch (cause: unknown) {
      setBanner(cause instanceof ApiError ? cause.message : 'Could not save that row.')
    } finally {
      setSaving(null)
    }
  }

  const downloadResult = async () => {
    if (!jobId) return
    setDownloading(true)
    setBanner(null)
    try {
      await jobsApi.downloadResult(jobId)
    } catch (cause: unknown) {
      setBanner(cause instanceof ApiError ? cause.message : 'Could not download the export.')
    } finally {
      setDownloading(false)
    }
  }

  const downloadErrors = async () => {
    if (!jobId) return
    setDownloading(true)
    setBanner(null)
    try {
      await jobsApi.downloadErrors(jobId)
    } catch (cause: unknown) {
      setBanner(cause instanceof ApiError ? cause.message : 'Could not download the error report.')
    } finally {
      setDownloading(false)
    }
  }

  const retryFailed = async () => {
    if (!jobId) return
    setRetrying(true)
    setBanner(null)
    try {
      const { jobId: nextJobId } = await jobsApi.retry(jobId)
      void refreshUser()
      // The retry is a new job; send the user to it rather than stale counters.
      window.location.assign(`/app/jobs/${nextJobId}`)
    } catch (cause: unknown) {
      setBanner(cause instanceof ApiError ? cause.message : 'Could not queue the retry.')
      setRetrying(false)
    }
  }

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const renderLine = (line: JobLine) => {
    const draft = drafts[line.id]
    const editable = line.status === 'Error'
    return (
      <tr className="hov" key={line.id}>
        <td>
          <input type="checkbox" checked={selected.has(line.id)} onChange={() => toggle(line.id)} />
        </td>
        <td className="n faint">{line.sourceRow}</td>
        {fields.map((field) => (
          <td className="mono" key={field}>
            {editable ? (
              <input
                className="inp mono"
                style={{ height: 30, width: 130 }}
                value={draft?.[field] ?? line.values[field] ?? ''}
                onChange={(event) =>
                  setDrafts((current) => ({
                    ...current,
                    [line.id]: { ...current[line.id], [field]: event.target.value },
                  }))
                }
              />
            ) : (
              (line.values[field] ?? <i className="faint">—</i>)
            )}
          </td>
        ))}
        <td>
          <LineStatusPill status={line.status} />
        </td>
        <td className="fs12 muted">
          {line.errorCode ? `${line.errorCode} · ` : ''}
          {line.errorMessage ?? '—'}
        </td>
        <td>
          {editable ? (
            <button
              className="btn btn-g btn-sm"
              type="button"
              disabled={!draft || saving === line.id}
              onClick={() => void applyEdit(line)}
            >
              {saving === line.id ? 'Saving…' : draft ? 'Apply' : 'Retry only'}
            </button>
          ) : (
            <span className="fs12 faint">—</span>
          )}
        </td>
      </tr>
    )
  }

  if (loading && !job) return <LoadingState label="Loading job…" />

  if (error && !job) return <ErrorState error={error} />
  if (!job) return null

  const failedCount = job.errorRows
  const selectedCount = selected.size

  return (
    <div className="wrap">
      <div className="phead">
        <div>
          <div className="crumb">
            <Link to="/app">Home</Link> / <Link to="/app/jobs">Jobs</Link> /{' '}
            <span className="mono">#{job.id.slice(0, 8)}</span>
          </div>
          <h2>{headline(job.type, job.status)}</h2>
          <p className="fs13 muted" style={{ margin: '6px 0 0' }}>
            <JobStatusPill status={job.status} />{' '}
            {job.entityType}
            {job.fileName ? ` · ${job.fileName}` : ''} · started{' '}
            {job.startedAt ? formatTime(job.startedAt) : '—'}
            {job.companyName ? ` · ${job.companyName}` : ''}
          </p>
        </div>
        <span className="spacer" />
        {job.errorRows > 0 ? (
          <button
            className="btn btn-o"
            type="button"
            disabled={downloading}
            onClick={() => void downloadErrors()}
          >
            {downloading ? 'Downloading…' : '↓ Error report'}
          </button>
        ) : null}
        {job.type === 'Import' ? (
          <button className="btn btn-d" type="button" onClick={() => setUndoOpen(true)}>
            Undo this import
          </button>
        ) : null}
      </div>

      <div className="sum" style={{ marginBottom: 18 }}>
        <div className="m">
          <span className="k">Succeeded</span>
          <span className="v ok">{formatInt(job.successRows)}</span>
        </div>
        <div className="m">
          <span className="k">Failed</span>
          <span className="v err">{formatInt(job.errorRows)}</span>
        </div>
        <div className="m">
          <span className="k">Skipped</span>
          <span className="v" style={{ color: 'var(--amber)' }}>
            {formatInt(job.skippedRows)}
          </span>
        </div>
        <div className="m">
          <span className="k">Credits used</span>
          <span className="v">{formatCredits(job.creditsUsed)}</span>
        </div>
        <span className="spacer" />
        <div className="fs12 faint" style={{ textAlign: 'right', lineHeight: 1.7 }}>
          {isJobLive(job.status) ? 'Still running' : 'Finished'}
          <br />
          <b className="mono" style={{ color: 'var(--ink-2)', fontSize: 13 }}>
            {job.finishedAt ? formatDateTime(job.finishedAt) : formatDateTime(job.createdAt)}
          </b>
        </div>
      </div>

      {job.errorMessage ? (
        <Alert tone="err" title="The job stopped early" style={{ marginBottom: 18 }}>
          {job.errorMessage}
        </Alert>
      ) : null}

      {banner ? (
        <Alert tone="err" title="That did not work" style={{ marginBottom: 18 }}>
          {banner}
        </Alert>
      ) : null}

      {job.type === 'Import' && failedCount > 0 ? (
        <Alert
          tone="warn"
          title="Failed rows can be fixed here and resubmitted"
          style={{ marginBottom: 18 }}
        >
          Edit the offending cell inline, then use <b>Retry failed</b>. Only the rows you changed are
          sent again — succeeded rows are never touched twice.
        </Alert>
      ) : null}

      {job.type === 'Delete' ? (
        <div className="card">
          <div className="card-h">
            <h3>Delete result</h3>
            <span className="spacer" />
            <div className="seg">
              {FILTERS.filter((value) => value !== 'Skipped').map((value) => (
                <button
                  type="button"
                  key={value}
                  className={filter === value ? 'on' : undefined}
                  onClick={() => setFilter(value)}
                >
                  {value === 'All'
                    ? `All ${formatInt(job.totalRows)}`
                    : value === 'Error'
                      ? `Rejected ${formatInt(job.errorRows)}`
                      : `Deleted ${formatInt(job.successRows)}`}
                </button>
              ))}
            </div>
          </div>

          {linesError ? (
            <ErrorState error={linesError} onRetry={() => void loadLines()} />
          ) : linesLoading ? (
            <LoadingState label="Loading deletion records…" />
          ) : lines.length === 0 ? (
            <EmptyState
              icon="✓"
              title="Nothing to show here"
              body={
                filter === 'Error'
                  ? 'No records were rejected in this job.'
                  : 'No records match this filter.'
              }
            />
          ) : (
            <div className="tscroll">
              <table>
                <thead>
                  <tr>
                    <th className="n">#</th>
                    <th>Reference</th>
                    <th>QBO ID</th>
                    <th>Result</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <tr className="hov" key={line.id}>
                      <td className="n faint">{line.sourceRow}</td>
                      <td className="mono">
                        {line.qboDocNumber ?? line.values.docNumber ?? (
                          <i className="faint">—</i>
                        )}
                      </td>
                      <td className="mono">{line.qboEntityId ?? <i className="faint">—</i>}</td>
                      <td>
                        <LineStatusPill status={line.status} />
                      </td>
                      <td className="fs12 muted">
                        {line.errorCode ? `${line.errorCode} · ` : ''}
                        {line.errorMessage ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="card-f">
            <span className="fs13 muted">
              <b>{formatInt(lines.length)}</b> target records processed
            </span>
            <span className="spacer" />
            <button className="btn btn-o btn-sm" type="button" disabled={downloading} onClick={() => void downloadErrors()}>
              {downloading ? 'Downloading…' : '↓ Error report'}
            </button>
          </div>
        </div>
      ) : job.type === 'Modify' ? (
        <div className="card">
          <div className="card-h">
            <h3>Modify result</h3>
            <span className="spacer" />
            <div className="seg">
              {FILTERS.filter((value) => value !== 'Skipped').map((value) => (
                <button
                  type="button"
                  key={value}
                  className={filter === value ? 'on' : undefined}
                  onClick={() => setFilter(value)}
                >
                  {value === 'All'
                    ? `All ${formatInt(job.totalRows)}`
                    : value === 'Error'
                      ? `Rejected ${formatInt(job.errorRows)}`
                      : `Updated ${formatInt(job.successRows)}`}
                </button>
              ))}
            </div>
          </div>

          {linesError ? (
            <ErrorState error={linesError} onRetry={() => void loadLines()} />
          ) : linesLoading ? (
            <LoadingState label="Loading modified records…" />
          ) : lines.length === 0 ? (
            <EmptyState
              icon="✓"
              title="Nothing to show here"
              body={
                filter === 'Error'
                  ? 'No records were rejected in this job.'
                  : 'No records match this filter.'
              }
            />
          ) : (
            <div className="tscroll">
              <table>
                <thead>
                  <tr>
                    <th className="n">#</th>
                    <th>QBO ID</th>
                    <th>Changed fields</th>
                    <th>Result</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <tr className="hov" key={line.id}>
                      <td className="n faint">{line.sourceRow}</td>
                      <td className="mono">{line.qboEntityId ?? <i className="faint">—</i>}</td>
                      <td className="fs12">
                        {Object.entries(line.values).length === 0 ? (
                          <i className="faint">—</i>
                        ) : (
                          Object.entries(line.values).map(([field, value]) => (
                            <div key={field}>
                              <b>{field}</b>: {value ?? <i className="faint">blank</i>}
                            </div>
                          ))
                        )}
                      </td>
                      <td>
                        <LineStatusPill status={line.status} />
                      </td>
                      <td className="fs12 muted">
                        {line.errorCode ? `${line.errorCode} · ` : ''}
                        {line.errorMessage ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="card-f">
            <span className="fs13 muted">
              <b>{formatInt(job.successRows)}</b> of {formatInt(job.totalRows)} records written
            </span>
            <span className="spacer" />
            <button className="btn btn-o btn-sm" type="button" disabled={downloading} onClick={() => void downloadErrors()}>
              {downloading ? 'Downloading…' : '↓ Error report'}
            </button>
          </div>
        </div>
      ) : job.type === 'Import' ? (
      <div className="card">
        <div className="card-h">
          <h3>All {formatInt(job.totalRows)} rows</h3>
          <span className="spacer" />
          <div className="seg">
            {FILTERS.map((value) => (
              <button
                type="button"
                key={value}
                className={filter === value ? 'on' : undefined}
                onClick={() => setFilter(value)}
              >
                {value === 'All'
                  ? `All ${formatInt(job.totalRows)}`
                  : value === 'Error'
                    ? `Failed ${formatInt(job.errorRows)}`
                    : value === 'Skipped'
                      ? `Skipped ${formatInt(job.skippedRows)}`
                      : `Succeeded ${formatInt(job.successRows)}`}
              </button>
            ))}
          </div>
        </div>

        {linesError ? (
          <ErrorState error={linesError} onRetry={() => void loadLines()} />
        ) : linesLoading ? (
          <LoadingState label="Loading rows…" />
        ) : lines.length === 0 ? (
          <EmptyState
            icon="✓"
            title="Nothing to show here"
            body={
              filter === 'Error'
                ? 'No rows failed in this job.'
                : 'No rows match this filter.'
            }
          />
        ) : (
          <div className="tscroll">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 34 }} />
                  <th className="n">Row</th>
                  {fields.map((field) => (
                    <th key={field}>{field}</th>
                  ))}
                  <th>Result</th>
                  <th>Message</th>
                  <th>Fix</th>
                </tr>
              </thead>
              <tbody>{lines.map(renderLine)}</tbody>
            </table>
          </div>
        )}

        <div className="card-f">
          <span className="fs13 muted">
            <b>{formatInt(selectedCount)}</b> rows selected
          </span>
          <span className="spacer" />
          <button
            className="btn btn-g btn-sm"
            type="button"
            disabled={selectedCount === 0}
            onClick={() => setSelected(new Set())}
          >
            Clear selection
          </button>
          <button
            className="btn btn-p btn-sm"
            type="button"
            disabled={retrying || failedCount === 0 || isJobLive(job.status)}
            onClick={() => void retryFailed()}
          >
            {retrying
              ? 'Queuing…'
              : `Retry failed (${formatInt(failedCount)}) · ${formatCredits(failedCount)} credits`}
          </button>
        </div>
      </div>
      ) : (
        <div className="card">
          <div className="card-h">
            <h3>Export summary</h3>
            <span className="spacer" />
            {job.resultAvailable ? (
              <button
                className="btn btn-p btn-sm"
                type="button"
                disabled={downloading}
                onClick={() => void downloadResult()}
              >
                {downloading ? 'Downloading…' : '↓ Download Excel'}
              </button>
            ) : null}
          </div>
          <div className="card-p" style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
            <div>
              <span className="fs12 faint">Records</span>
              <div className="fs18" style={{ fontWeight: 700 }}>
                {formatInt(job.successRows)}
              </div>
            </div>
            <div>
              <span className="fs12 faint">Credits used</span>
              <div className="fs18" style={{ fontWeight: 700 }}>
                {formatCredits(job.creditsUsed)}
              </div>
            </div>
            {job.errorMessage ? (
              <div className="fs13" style={{ color: 'var(--red)', alignSelf: 'center' }}>
                {job.errorMessage}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {job.log?.length ? (
        <div className="card" style={{ marginTop: 18 }}>
          <div className="card-h">
            <h3>Job log</h3>
            <span className="fs12 faint">last {job.log.length} entries</span>
          </div>
          <div className="card-p" style={{ padding: 0 }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 150 }}>Time</th>
                  <th style={{ width: 110 }}>Level</th>
                  <th>Event</th>
                </tr>
              </thead>
              <tbody>
                {job.log.map((entry, index) => (
                  <tr key={`${entry.timestamp}-${index}`}>
                    <td className="mono fs12">{formatTime(entry.timestamp)}</td>
                    <td>
                      <StatusPill tone={LOG_TONE[entry.level]} label={entry.level.toLowerCase()} />
                    </td>
                    <td className="fs13">{entry.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="row" style={{ marginTop: 20 }}>
        <Link className="btn btn-o" to="/app">
          ← Back to home
        </Link>
        <span className="spacer" />
        {job.type === 'Import' ? (
          <Link className="btn btn-g" to="/app/import">
            Import another file
          </Link>
        ) : job.type === 'Delete' ? (
          <Link className="btn btn-g" to="/app/delete">
            Start another delete
          </Link>
        ) : job.type === 'Modify' ? (
          <Link className="btn btn-g" to="/app/modify">
            Modify more records
          </Link>
        ) : (
          <Link className="btn btn-g" to="/app/export">
            Start another export
          </Link>
        )}
      </div>

      {job.type === 'Import' ? (
        <UndoModal job={job} open={undoOpen} onClose={() => setUndoOpen(false)} />
      ) : null}
    </div>
  )
}

/** The contract has no undo endpoint yet, so this confirms the design and the
 *  billing rate (0.5 credits per deleted record) without firing a request. */
function UndoModal({
  job,
  open,
  onClose,
}: {
  job: { id: string; successRows: number; companyName: string | null }
  open: boolean
  onClose: () => void
}) {
  return (
    <Modal
      open={open}
      title={`Undo import #${job.id.slice(0, 8)}?`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-g" type="button" onClick={onClose}>
            Keep the import
          </button>
          <span className="spacer" />
          <button className="btn btn-d" type="button" disabled>
            Undo {formatInt(job.successRows)} records
          </button>
        </>
      }
    >
      <p className="fs13" style={{ margin: '0 0 16px', lineHeight: 1.7 }}>
        This removes the <b>{formatInt(job.successRows)} records</b> this job created in{' '}
        <b>{job.companyName ?? 'this company'}</b>. Anything you changed in QuickBooks afterwards is
        not restored.
      </p>
      <Alert tone="warn" title="Undo is not live yet" style={{ margin: '0 0 16px' }}>
        The rollback endpoint is still being built. Records already linked to payments would fail to
        delete and stay in QuickBooks.
      </Alert>
      <p className="fs13 muted" style={{ margin: 0 }}>
        Undo is billed as a delete:{' '}
        <b className="mono">
          {formatInt(job.successRows)} × 0.5 = {formatCredits(job.successRows * 0.5)} credits
        </b>
        .
      </p>
    </Modal>
  )
}
